import { useMemo } from 'react';

import { useOptionalCurrentUser } from './auth-provider.tsx';
import type {
  CurrentUserResponseDto,
  CurrentUserResponseDtoPartnersItemPermissionsSections,
} from './generated/schemas/index.ts';
import { resolveActivePartner } from './intercom-identity.ts';

/**
 * Permissions v1 — per user, per factory, per section of the hub. The backend
 * resolves every level (a missing grant is its default, Artic staff are
 * exempt), so the frontend only reads them off `/current-user`.
 */
export type SectionLevels = CurrentUserResponseDtoPartnersItemPermissionsSections;
export type PermissionSection = keyof SectionLevels;
export type PermissionLevel = SectionLevels[PermissionSection];

export const PERMISSION_SECTIONS = [
  'quality',
  'planner',
  'catalog',
  'materials',
  'cost_to_produce',
  'settings',
  'partner_api',
] as const satisfies readonly PermissionSection[];

export const PERMISSION_LEVELS = [
  'off',
  'read',
  'write',
] as const satisfies readonly PermissionLevel[];

const LEVEL_RANK: Record<PermissionLevel, number> = { off: 0, read: 1, write: 2 };

export function levelAtLeast(level: PermissionLevel, min: PermissionLevel): boolean {
  return LEVEL_RANK[level] >= LEVEL_RANK[min];
}

/**
 * The user's level for `section` in the active partner: that factory's level
 * when `factoryId` is given and listed, otherwise the partner-wide maximum.
 * Pure, so route `beforeLoad` guards can call it with the cached user.
 */
export function resolveSectionLevel(
  user: CurrentUserResponseDto,
  activePartner: string | undefined,
  section: PermissionSection,
  factoryId?: string,
): PermissionLevel {
  if (user.is_permission_exempt) return 'write';
  const partner = resolveActivePartner(user.partners, activePartner);
  if (!partner) return 'write';
  const { permissions } = partner;
  if (factoryId) {
    const factory = permissions.factories.find((f) => f.factory_id === factoryId);
    if (factory) return factory.levels[section];
  }
  return permissions.sections[section];
}

/**
 * The factories the user can at least read for `section`, or `'all'` when no
 * factory is restricted (Artic staff, or no per-factory data).
 */
export function resolveReadableFactoryIds(
  user: CurrentUserResponseDto,
  activePartner: string | undefined,
  section: PermissionSection,
): ReadonlySet<string> | 'all' {
  if (user.is_permission_exempt) return 'all';
  const partner = resolveActivePartner(user.partners, activePartner);
  const factories = partner?.permissions.factories ?? [];
  if (factories.length === 0) return 'all';
  if (factories.every((f) => f.levels[section] !== 'off')) return 'all';
  return new Set(factories.filter((f) => f.levels[section] !== 'off').map((f) => f.factory_id));
}

/**
 * The factories the user can access at all, or `'all'` when none is
 * restricted. Per-factory levels are already off where access is revoked, so
 * `resolveReadableFactoryIds` drops those factories too.
 */
export function resolveFactoryAccess(
  user: CurrentUserResponseDto,
  activePartner: string | undefined,
): ReadonlySet<string> | 'all' {
  if (user.is_permission_exempt) return 'all';
  const partner = resolveActivePartner(user.partners, activePartner);
  const factories = partner?.permissions.factories ?? [];
  if (factories.every((f) => f.has_access)) return 'all';
  return new Set(factories.filter((f) => f.has_access).map((f) => f.factory_id));
}

/** Whether the user can access any factory of the active partner (gates the Factory nav). */
export function resolveHasFactoryAccess(
  user: CurrentUserResponseDto,
  activePartner: string | undefined,
): boolean {
  if (user.is_permission_exempt) return true;
  const partner = resolveActivePartner(user.partners, activePartner);
  return partner?.permissions.has_factory_access ?? true;
}

interface SectionOptions {
  /** Check this factory's level instead of the partner-wide maximum. */
  factoryId?: string | null;
}

/**
 * The current user's level for a section. Outside an `<AuthProvider>` (the
 * hub's `/current-user` failure fallback) this fails open to `'write'`, the
 * same way feature flags render with an empty context there.
 */
export function useSectionLevel(
  section: PermissionSection,
  options?: SectionOptions,
): PermissionLevel {
  const auth = useOptionalCurrentUser();
  if (!auth) return 'write';
  return resolveSectionLevel(
    auth.user,
    auth.activePartner,
    section,
    options?.factoryId ?? undefined,
  );
}

export interface SectionPermission {
  level: PermissionLevel;
  canRead: boolean;
  canWrite: boolean;
}

/**
 * The current user's level for a section plus the two checks views need, from
 * one call. Fails open to write outside an `<AuthProvider>`, like `useSectionLevel`.
 */
export function useSectionPermission(
  section: PermissionSection,
  options?: SectionOptions,
): SectionPermission {
  const level = useSectionLevel(section, options);
  return {
    level,
    canRead: levelAtLeast(level, 'read'),
    canWrite: levelAtLeast(level, 'write'),
  };
}

export function useCanRead(section: PermissionSection, options?: SectionOptions): boolean {
  return levelAtLeast(useSectionLevel(section, options), 'read');
}

export function useCanWrite(section: PermissionSection, options?: SectionOptions): boolean {
  return levelAtLeast(useSectionLevel(section, options), 'write');
}

/**
 * Factories the user can read for `section` — filter factory pickers with it.
 * Stable identity across renders while the user's permissions are unchanged.
 */
export function useReadableFactoryIds(section: PermissionSection): ReadonlySet<string> | 'all' {
  const auth = useOptionalCurrentUser();
  return useMemo(
    () => (auth ? resolveReadableFactoryIds(auth.user, auth.activePartner, section) : 'all'),
    [auth?.user, auth?.activePartner, section],
  );
}

/** Factories the user can access at all — filter factory pickers that are not section-scoped. */
export function useAccessibleFactoryIds(): ReadonlySet<string> | 'all' {
  const auth = useOptionalCurrentUser();
  return useMemo(
    () => (auth ? resolveFactoryAccess(auth.user, auth.activePartner) : 'all'),
    [auth?.user, auth?.activePartner],
  );
}

/** True when the user can access at least one factory; fails open outside an AuthProvider. */
export function useHasFactoryAccess(): boolean {
  const auth = useOptionalCurrentUser();
  return auth ? resolveHasFactoryAccess(auth.user, auth.activePartner) : true;
}
